import "./App.css";
import "./assets/styles/responsive.css";
import "./utils/i18LanguageTranslation/i18n";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import React, { useContext, useEffect } from "react";
import { BackButtonControl, ToastWrapper } from "components/atoms";
import CenterModal from "components/atoms/CenterModal";
import SidebarDrawer from "components/atoms/SidebarDrawer";
import SecondCenterModal from "components/atoms/SecondCenterModal";
import DriveNavigation from "navigation/DriveNavigation";
import { useNotificationNavigation } from "hooks/useNotificationNavigation";
import useRemoteConfig from "hooks/useRemoteConfig";
import { FirebaseContext } from "context/FirebaseContext";
import { useSelector } from "react-redux";
import ProtectedRoute from "components/atoms/ProtectedRoute";
import SocketProvider from "utils/Socket/socketProvider";
import { isLogoutInProgress } from "utils/uploadController";

const NotificationInitializer = () => {
  useNotificationNavigation();
  return null;
};

const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { config: remoteConfigValues, loading: remoteConfigLoading } =
    useRemoteConfig();
  const { setSetting, setConfigData } = useContext(FirebaseContext);
  const { uploadNewFile, uploadFolderState } = useSelector(
    (state) => state.general,
  );
  const currentUserData = useSelector((state) => state.auth.user);

  const user = {
    id: currentUserData?.id,
    email: currentUserData?.email,
    deviceId: currentUserData?.deviceId,
  };

  useEffect(() => {
    sessionStorage.removeItem("bigfoot_chunk_reload_attempted");
  }, []);

  useNotificationNavigation();

  // Push parsed remote config into FirebaseContext whenever it changes
  // (covers both the initial fetch and any real-time update).
  useEffect(() => {
    if (remoteConfigLoading) return;

    setConfigData({
      country: remoteConfigValues.country ?? "",
      language: remoteConfigValues.language ?? "",
      color: remoteConfigValues.color ?? "",
    });

    setSetting((prev) => ({
      ...prev,
      default_country: remoteConfigValues.default_country ?? "",
      maxFileSize: remoteConfigValues.drive_max_file_size ?? 0,
      maxFileCount: remoteConfigValues.drive_select_file_limit ?? 0,
      maxProfileFileSize: remoteConfigValues.max_file_size ?? 0,
      driveShareUserLimit: remoteConfigValues.drive_share_user_limit ?? null,
    }));
  }, [remoteConfigValues, remoteConfigLoading, setConfigData, setSetting]);

  // Redirect to / away from maintenance page based on remote config flag
  useEffect(() => {
    if (remoteConfigLoading) return;

    const featureEnabled = Boolean(remoteConfigValues.is_drive_maintenance);

    if (featureEnabled) {
      if (location.pathname !== "/drive/server-maintenance") {
        sessionStorage.setItem("preMaintenancePath", location.pathname);
      }

      navigate("/drive/server-maintenance", { replace: true });
    } else {
      if (location.pathname === "/drive/server-maintenance") {
        const previousPath =
          sessionStorage.getItem("preMaintenancePath") ?? "/drive/home";

        sessionStorage.removeItem("preMaintenancePath");

        navigate(previousPath);
      }
    }
  }, [
    remoteConfigLoading,
    remoteConfigValues.is_drive_maintenance,
    location.pathname,
    navigate,
  ]);

  // Warn users when uploads are in progress (skipped during a confirmed logout)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isLogoutInProgress()) return;

      const hasUploadingFiles = uploadNewFile?.list?.some(
        (file) => file.status === "uploading",
      );
      const isFolderUploading = uploadFolderState?.status === "uploading";

      if (hasUploadingFiles || isFolderUploading) {
        const message =
          "Are you sure you want to leave? Files are still being uploaded.";
        e.preventDefault();
        e.returnValue = message; // Required for modern browsers
        return message;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [uploadNewFile, uploadFolderState]);

  const useDefaultScrollbar = currentUserData?.default_scrollbar === "true";

  useEffect(() => {
    if (useDefaultScrollbar) {
      document.documentElement.setAttribute("data-scrollbar", "default");
    } else {
      document.documentElement.removeAttribute("data-scrollbar");
    }
  }, [useDefaultScrollbar]);

  return (
    <>
      <NotificationInitializer />
      {/* <DrawerRight /> */}
      <CenterModal />
      <SecondCenterModal />
      <SidebarDrawer />
      <BackButtonControl prevPath={location.pathname} />
      <ToastWrapper />
      <SocketProvider user={user}>
        <Routes>
          <Route element={<ProtectedRoute />}>
            <Route index element={<Navigate to="/drive/home" replace />} />
            <Route path="/drive/*" element={<DriveNavigation />} />
          </Route>
        </Routes>
      </SocketProvider>
    </>
  );
};

export default App;
